import React, { useState, useEffect } from 'react';
import {
  Modal, Button, Form, Row, Col, Card, Badge,
  Spinner, InputGroup, Alert
} from 'react-bootstrap';
import QuestionBankService from '../services/questionBankService';

// Defined at module level to prevent remounting on every QuestionBankBrowser render
const QuestionCard = ({ question, isSelected, onToggle }) => (
  <Card
    className={`mb-2 cursor-pointer ${isSelected ? 'border-primary border-2' : ''}`}
    onClick={() => onToggle(question)}
    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
  >
    <Card.Body>
      <div className="d-flex justify-content-between align-items-start">
        <div className="flex-grow-1">
          <h6 className="mb-2">{question.question}</h6>
          <div className="mt-2">
            <Badge bg="info" className="me-2">{question.type || question.questionType}</Badge>
            <Badge bg="secondary" className="me-2">{question.difficulty || 'medium'}</Badge>
            {question.category && (
              <Badge bg="success" className="me-2">{question.category}</Badge>
            )}
            <Badge bg="light" text="dark">{question.points || 1} pt{(question.points || 1) !== 1 ? 's' : ''}</Badge>
          </div>

          {question.tags && question.tags.length > 0 && (
            <div className="mt-2">
              {question.tags.map((tag, idx) => (
                <Badge key={idx} bg="outline-secondary" className="me-1" style={{ fontSize: '0.7rem' }}>
                  #{tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          className="ms-3"
        />
      </div>

      {(question.type === 'multiple-choice' || question.questionType === 'multiple-choice') && question.options && (
        <div className="mt-2 small text-muted">
          <strong>Options:</strong> {question.options.slice(0, 2).join(', ')}
          {question.options.length > 2 && ` (+${question.options.length - 2} more)`}
        </div>
      )}

      {question.explanation && (
        <div className="mt-2 small text-muted">
          <strong>Explanation:</strong> {question.explanation.substring(0, 100)}
          {question.explanation.length > 100 && '...'}
        </div>
      )}

      <div className="mt-2 small text-muted">
        <span>Created by {question.createdByName || 'Unknown'}</span>
        {question.usageCount > 0 && (
          <span className="ms-3">• Used {question.usageCount} time{question.usageCount !== 1 ? 's' : ''}</span>
        )}
      </div>
    </Card.Body>
  </Card>
);

/**
 * Question Bank Browser Component
 * Allows browsing and selecting questions from the bank
 */
const QuestionBankBrowser = ({ show, onHide, onSelectQuestions, currentUser }) => {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    difficulty: '',
    questionType: '',
    searchTerm: ''
  });
  const [categories, setCategories] = useState([]);

  const questionBankService = new QuestionBankService();

  useEffect(() => {
    if (show) {
      loadQuestions();
      loadCategories();
    }
  }, [show, filters]);

  const loadQuestions = async () => {
    setLoading(true);
    const results = await questionBankService.searchQuestions(filters);
    setQuestions(results);
    setLoading(false);
  };

  const loadCategories = async () => {
    const cats = await questionBankService.getCategories();
    setCategories(cats);
  };

  const toggleQuestionSelection = (question) => {
    const isSelected = selectedQuestions.some(q => q.id === question.id);
    if (isSelected) {
      setSelectedQuestions(selectedQuestions.filter(q => q.id !== question.id));
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const handleAddToQuiz = () => {
    // Increment usage count for selected questions
    selectedQuestions.forEach(q => {
      questionBankService.incrementUsage(q.id);
    });

    onSelectQuestions(selectedQuestions);
    setSelectedQuestions([]);
    onHide();
  };

  const handleClearFilters = () => {
    setFilters({
      category: '',
      difficulty: '',
      questionType: '',
      searchTerm: ''
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Question Bank</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Filters */}
        <Card className="mb-3">
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-2 mb-md-0">
                  <Form.Label className="small">Category</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.category}
                    onChange={(e) => setFilters({...filters, category: e.target.value})}
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-2 mb-md-0">
                  <Form.Label className="small">Difficulty</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.difficulty}
                    onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
                  >
                    <option value="">All Difficulties</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-2 mb-md-0">
                  <Form.Label className="small">Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.questionType}
                    onChange={(e) => setFilters({...filters, questionType: e.target.value})}
                  >
                    <option value="">All Types</option>
                    <option value="multiple-choice">Multiple Choice</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="true-false">True/False</option>
                    <option value="short-answer">Short Answer</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-2 mb-md-0">
                  <Form.Label className="small">Search</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="text"
                      placeholder="Search..."
                      value={filters.searchTerm}
                      onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {(filters.category || filters.difficulty || filters.questionType || filters.searchTerm) && (
              <div className="mt-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Selected Questions Count */}
        {selectedQuestions.length > 0 && (
          <Alert variant="info" className="py-2">
            <strong>{selectedQuestions.length}</strong> question{selectedQuestions.length !== 1 ? 's' : ''} selected
          </Alert>
        )}

        {/* Questions List */}
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <Alert variant="info">
              <h6>No questions found</h6>
              <p className="mb-0">
                {filters.category || filters.difficulty || filters.questionType || filters.searchTerm
                  ? 'Try adjusting your filters or search term.'
                  : 'No questions have been added to the bank yet. Save questions from quizzes to build your question bank.'}
              </p>
            </Alert>
          ) : (
            questions.map(question => (
              <QuestionCard
                key={question.id}
                question={question}
                isSelected={selectedQuestions.some(q => q.id === question.id)}
                onToggle={toggleQuestionSelection}
              />
            ))
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div className="text-muted small">
          {questions.length} question{questions.length !== 1 ? 's' : ''} available
        </div>
        <div>
          <Button variant="secondary" onClick={onHide} className="me-2">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddToQuiz}
            disabled={selectedQuestions.length === 0}
          >
            Add {selectedQuestions.length > 0 && `${selectedQuestions.length} `}
            Question{selectedQuestions.length !== 1 ? 's' : ''} to Quiz
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionBankBrowser;
